import { requestLoginCode } from "../../api/auth";
import { createElement } from "../../utils/domHelpers";
import { validateEmail } from "../../utils/helpers";

export type LoginCodePanelOptions = {
	getEmail: () => string;
	onSubmit: (email: string, code: string) => Promise<void>;
	onModeChange?: (codeMode: boolean) => void;
};

export function createLoginCodePanel(
	options: LoginCodePanelOptions,
): HTMLDivElement {
	const panel = createElement("div", {
		className: "mt-2 text-start w-100",
	});
	panel.innerHTML = `
		<button type="button" class="btn btn-link p-0 text-info" data-login-code-toggle>
			Login with a one time code
		</button>
		<div class="mt-2 d-none" data-login-code-entry>
			<label class="form-label" data-login-code-label>One-time code</label>
			<div class="input-group">
				<input class="form-control" type="text" inputmode="numeric" autocomplete="one-time-code"
					maxlength="6" aria-label="6-digit one-time code" data-login-code-input disabled>
				<button type="button" class="btn btn-primary" data-login-code-submit>Send me a code</button>
			</div>
			<div class="small mt-2" role="status" aria-live="polite" data-login-code-message></div>
			<button type="button" class="btn btn-link p-0 mt-2 text-info d-none" data-login-code-send>
				Resend code to my email
			</button>
		</div>
	`;

	const toggleButton = panel.querySelector<HTMLButtonElement>(
		"[data-login-code-toggle]",
	)!;
	const sendButton = panel.querySelector<HTMLButtonElement>(
		"[data-login-code-send]",
	)!;
	const message = panel.querySelector<HTMLDivElement>(
		"[data-login-code-message]",
	)!;
	const entry = panel.querySelector<HTMLDivElement>("[data-login-code-entry]")!;
	const codeInput = panel.querySelector<HTMLInputElement>(
		"[data-login-code-input]",
	)!;
	const submitButton = panel.querySelector<HTMLButtonElement>(
		"[data-login-code-submit]",
	)!;
	let cooldownTimer: number | undefined;
	let codeMode = false;
	let codeAvailable = false;

	const startCooldown = (seconds: number): void => {
		if (cooldownTimer !== undefined) window.clearInterval(cooldownTimer);
		const deadline = Date.now() + Math.max(0, seconds) * 1000;
		const update = (): void => {
			const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
			sendButton.disabled = remaining > 0;
			if (remaining <= 0) {
				if (cooldownTimer !== undefined) {
					window.clearInterval(cooldownTimer);
					cooldownTimer = undefined;
				}
				sendButton.textContent = "Resend code to my email";
				return;
			}
			sendButton.textContent = `Send another code in ${remaining}s`;
		};
		update();
		if (deadline > Date.now()) {
			cooldownTimer = window.setInterval(update, 1000);
		}
	};

	toggleButton.addEventListener("click", () => {
		codeMode = !codeMode;
		options.onModeChange?.(codeMode);
		entry.classList.toggle("d-none", !codeMode);
		codeInput.disabled = !codeMode || !codeAvailable;
		toggleButton.textContent = codeMode
			? "Login with a password"
			: "Login with a one time code";
		if (codeMode && !options.onModeChange) {
			toggleButton.classList.add("d-none");
		}
		if (codeMode) {
			(codeAvailable ? codeInput : submitButton).focus();
		}
	});

	const enableCodeEntry = (): void => {
		codeAvailable = true;
		codeInput.disabled = !codeMode;
		submitButton.textContent = "Sign in with code";
		sendButton.classList.remove("d-none");
	};

	const sendCode = async (): Promise<void> => {
		const email = options.getEmail().trim();
		if (!validateEmail(email)) {
			message.textContent = "Enter a valid email address first.";
			message.className = "small mt-2 text-danger";
			return;
		}

		sendButton.disabled = true;
		sendButton.textContent = "Sending…";
		submitButton.disabled = true;
		if (!codeAvailable) submitButton.textContent = "Sending…";
		message.textContent = "";
		try {
			const retryAfter = await requestLoginCode(email);
			enableCodeEntry();
			message.textContent =
				"If an account exists for this email, a code is on its way. Check your inbox and junk mail.";
			message.className = "small mt-2 text-success";
			startCooldown(retryAfter);
			if (codeMode) codeInput.focus();
		} catch (error) {
			const retryAfter =
				typeof error === "object" &&
				error !== null &&
				"retryAfterSeconds" in error &&
				typeof error.retryAfterSeconds === "number"
					? error.retryAfterSeconds
					: 0;
			message.textContent =
				error instanceof Error
					? error.message
					: "Could not request a sign-in code. Please try again.";
			message.className = "small mt-2 text-danger";
			if (retryAfter > 0) {
				enableCodeEntry();
				startCooldown(retryAfter);
				if (codeMode) codeInput.focus();
			} else {
				sendButton.disabled = false;
				sendButton.textContent = "Resend code to my email";
				submitButton.textContent = codeAvailable
					? "Sign in with code"
					: "Send me a code";
			}
		} finally {
			submitButton.disabled = false;
		}
	};

	sendButton.addEventListener("click", sendCode);

	codeInput.addEventListener("keydown", (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			if (!event.repeat) submitButton.click();
		}
	});

	submitButton.addEventListener("click", async () => {
		if (!codeAvailable) {
			await sendCode();
			return;
		}
		const email = options.getEmail().trim();
		const code = codeInput.value.trim();
		if (!validateEmail(email)) {
			message.textContent = "Enter a valid email address first.";
			message.className = "small mt-2 text-danger";
			return;
		}
		if (!/^[0-9]{6}$/.test(code)) {
			message.textContent = "Enter the 6-digit code from your email.";
			message.className = "small mt-2 text-danger";
			codeInput.focus();
			return;
		}

		submitButton.disabled = true;
		submitButton.textContent = "Signing in…";
		message.textContent = "";
		try {
			await options.onSubmit(email, code);
			message.textContent = "Signed in successfully.";
			message.className = "small mt-2 text-success";
		} catch (error) {
			message.textContent =
				error instanceof Error
					? error.message
					: "Sign-in failed. Please try again.";
			message.className = "small mt-2 text-danger";
			submitButton.disabled = false;
			submitButton.textContent = "Sign in with code";
		}
	});

	return panel;
}
